import {useEffect, useRef, useState, type PointerEvent} from 'react';

import {useFillSurface} from './FillSurface';
import {useControlFrame} from './renderers/control-frame';

/**
 * Canvas signature pad used by the interactive fill mode. Captures the
 * signature as a base64 PNG data URL via `onChange`; `value` (if present)
 * is restored on mount. Fills its parent box, so the caller sizes it.
 */
interface SignaturePadProps {
  value: string | undefined;
  onChange: (dataUrl: string) => void;
}

export const SignaturePad = ({value, onChange}: SignaturePadProps) => {
  /**
   * The pad's own chrome comes off on the export: the frame it draws to say
   * "sign here", and the Clear button, which is an offer nobody can take on
   * paper. The canvas itself stays, so the signature and the geometry around it
   * are unchanged — see `inputFrame`.
   */
  const {forExport} = useFillSurface();
  const frame = useControlFrame();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [hasInk, setHasInk] = useState(Boolean(value));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    canvas.width = Math.max(1, cssWidth) * ratio;
    canvas.height = Math.max(1, cssHeight) * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, cssWidth, cssHeight);
      img.src = value;
    }
  }, [value]);

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {x: e.clientX - rect.left, y: e.clientY - rect.top};
  };

  const start = (e: PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    e.stopPropagation();
    drawing.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    const {x, y} = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const {x, y} = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current && canvasRef.current) {
      onChange(canvasRef.current.toDataURL('image/png'));
      dirty.current = false;
    }
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange('');
  };

  return (
    // The pad fills its box and keeps that box's size, always. Clear used to be
    // a sibling below the canvas in a column, so the moment someone started
    // signing the button appeared and the canvas lost height under their hand --
    // the drawing surface shrinking mid-stroke. It overlays the corner now, so
    // the geometry never changes and what was already drawn stays where it was.
    <div className="relative h-full w-full">
      <canvas
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        className={`h-full w-full touch-none rounded bg-white ${frame('open')}`}
      />
      {hasInk && !forExport ? (
        <button
          type="button"
          onClick={clear}
          // Stops a stroke starting on the button from drawing underneath it.
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute top-1 right-1 rounded bg-white/80 px-1 text-[9px] text-gray-400 hover:text-gray-600"
        >
          Clear
        </button>
      ) : null}
    </div>
  );
};
